// ========================================
// Formaciones Management - Core & List View
// ========================================

const Formaciones = {

    currentFormationId: null,
    currentFilter: 'all', // State for filtering
    currentStudyPlanId: 'cine', // Default study plan to show
    currentCategory: null, // formation type shown in Plan de Estudios (first active type by default)
    currentStudyPlanTab: 'quadrant', // 'quadrant' | 'config' | 'plans' | 'types'

    // School data (database): Plan de Estudios → Planes. Ids are stable; names are editable.
    get STUDY_PLANS() {
        return typeof StudyPlanService !== 'undefined' ? StudyPlanService.getPlans() : [];
    },

    ACADEMIC_YEARS: [
        { id: '1', name: '1º' },
        { id: '2', name: '2º' },
        { id: '3', name: '3º' }
    ],

    STUDY_BLOCKS: [
        { id: 'LENGUA', name: 'LENGUA / CULTURA' },
        { id: 'PROTOCOLOS', name: 'PROTOCOLOS / METODOLOGÍA / TÉCNICA' },
        { id: 'PRACTICA', name: 'PRÁCTICA' },
        { id: 'OTROS', name: 'OTROS' }
    ],

    getBlocksForCategory(category) {
        if (typeof DataManager !== 'undefined' && DataManager.getStudyBlocks) {
            return DataManager.getStudyBlocks(category || this.currentCategory || FormationTypeService.firstId());
        }
        return this.STUDY_BLOCKS;
    },

    // OTROS is where unclassified subjects live; its id stays, people read «Sin bloque».
    blockLabel(block) {
        return block.id === 'OTROS' ? 'Sin bloque' : block.name;
    },

    // Formation types (Plan de Estudios → Tipos): their study plans and courses, from the database.
    get FORMATION_CONFIG() {
        if (typeof FormationTypeService === 'undefined') return {};
        return Object.fromEntries(FormationTypeService.getTypes().filter(type => type.active).map(type => [type.id, {
            name: type.name, pluralName: type.pluralName, icon: type.icon,
            studyPlans: typeof StudyPlanService !== 'undefined' ? StudyPlanService.byCategory(type.id) : [],
            years: FormationTypeService.years(type.id)
        }]));
    },

    // Class count and hours per formation or subject this course, computed by the server.
    currentClassSummary(groupBy) {
        if (typeof FileStorage === 'undefined' || !FileStorage.needClassSummary) return null;
        const hash = App.currentRoute();
        const rows = FileStorage.needClassSummary({ groupBy, ...FileStorage.classBlockRange(FileStorage.classBlock(new Date())) }, () => {
            if (App.currentRoute() === hash) App.navigate(hash);
        });
        return rows ? new Map(rows.map(row => [String(row.id), row])) : null;
    },

    // Cuadrante académico, diseño A (2026-10-26): cifras arriba y, por bloque, cada asignatura con
    // «programadas / previstas» y una barra en cada curso. Los cursos salen del plan (no fijos en 1º–3º).
    // Estilos: assets/css/views/formaciones.css (.pe-*).
    studyPlanQuadrant(studyPlanId) {
        const formations = DataManager.getFormations().filter(f => f.studyPlanId === studyPlanId);
        const category = this.currentCategory || FormationTypeService.firstId();
        let years = (typeof StudyPlanService !== 'undefined' ? StudyPlanService.years(studyPlanId, category) : []) || [];
        if (!years.length) years = [...new Set(formations.map(f => String(f.academicYear || '1')))].sort().map(id => ({ id, name: `${id}º` }));
        const yearIds = years.map(y => String(y.id));
        const yearOf = f => {
            const id = String(f.academicYear || '');
            return yearIds.includes(id) ? id : (yearIds.length === 1 ? yearIds[0] : null);
        };

        const categoryBlocks = this.getBlocksForCategory(category);
        const blocks = new Map(categoryBlocks.map(b => [b.id, { ...b, subjects: new Map() }]));
        const otros = () => {
            if (!blocks.has('OTROS')) blocks.set('OTROS', { id: 'OTROS', name: 'OTROS', color: '#64748b', subjects: new Map() });
            return blocks.get('OTROS');
        };
        const subjectSummary = this.currentClassSummary('subject');
        formations.forEach(f => {
            const year = yearOf(f);
            if (!year) return;
            DataManager.getSubjectsByFormation(f.id)
                .filter(s => !['VAC', 'EXT'].includes(s.type?.toUpperCase()))
                .forEach(s => {
                    const block = blocks.get(s.studyBlock) || otros();
                    const key = Utils.normalizeString(String(s.name || '').trim());
                    if (!block.subjects.has(key)) block.subjects.set(key, { name: String(s.name || '').trim(), ideal: {}, done: {} });
                    const row = block.subjects.get(key);
                    // Keep the accented spelling when two formations write the same subject differently.
                    if (/[áéíóúñ]/i.test(s.name) && !/[áéíóúñ]/i.test(row.name)) row.name = String(s.name).trim();
                    const done = subjectSummary
                        ? (subjectSummary.get(String(s.id))?.hours || 0)
                        : DataManager.getClassesBySubject(s.id).reduce((total, cls) => total + Utils.calculateDuration(cls.startTime, cls.endTime), 0);
                    row.ideal[year] = (row.ideal[year] || 0) + (parseInt(s.totalHours) || 0);
                    row.done[year] = (row.done[year] || 0) + done;
                });
        });
        return { formations, years, blocks: [...blocks.values()].filter(b => b.subjects.size) };
    },

    renderStudyPlan(studyPlanId) {
        const studyPlan = this.STUDY_PLANS.find(p => p.id === studyPlanId);
        if (!studyPlan) return '<p class="pe-empty">Este tipo aún no tiene planes de estudios. Créalos en la pestaña «Planes».</p>';
        const { formations, years, blocks } = this.studyPlanQuadrant(studyPlanId);
        if (formations.length === 0) return '<p class="pe-empty">No hay formaciones asignadas a este plan de estudios aún.</p>';

        const sum = obj => Object.values(obj).reduce((a, b) => a + b, 0);
        const h = n => Math.round(n * 10) / 10;
        let ideal = 0, done = 0, missing = 0, missingSubjects = 0, subjects = 0;
        const over = [];
        const perYear = Object.fromEntries(years.map(y => [y.id, { ideal: 0, done: 0 }]));
        blocks.forEach(b => b.subjects.forEach(s => {
            subjects++;
            let gap = 0;
            years.forEach(y => {
                const i = s.ideal[y.id] || 0, d = s.done[y.id] || 0;
                perYear[y.id].ideal += i; perYear[y.id].done += d;
                if (i && d > i) over.push(`${s.name} ${y.name} (+${h(d - i)} h)`);
                gap += Math.max(0, i - d);
            });
            ideal += sum(s.ideal); done += sum(s.done); missing += gap;
            if (gap > 0) missingSubjects++;
        }));

        const cell = (i, d) => {
            if (!i && !d) return '<td class="pe-td"><span class="pe-none">—</span></td>';
            const state = !i || d > i ? 'is-over' : d >= i ? 'is-done' : d > 0 ? 'is-going' : 'is-zero';
            const pct = i ? Math.min(100, Math.round(d / i * 100)) : 100;
            return `<td class="pe-td"><div class="pe-cell ${state}"><span class="pe-n">${h(d)} / ${h(i)} h</span><span class="pe-meter" aria-hidden="true"><i style="width: ${pct}%;"></i></span></div></td>`;
        };
        const sumCell = (i, d) => `<td class="pe-td"><span class="pe-n">${h(d)} / ${h(i)} h</span></td>`;
        const color = c => /^#[0-9a-f]{6}$/i.test(c || '') ? c : 'var(--dnr-line-strong)';
        const stat = (label, value, note, tone = '') => `
            <div class="pe-stat ${tone}"><span class="pe-stat-l">${label}</span><span class="pe-stat-n">${value}</span><span class="pe-stat-s">${Utils.sanitizeHTML(note)}</span></div>`;

        return `
            <div class="pe-stats">
                ${stat('Horas previstas', h(ideal), `${years.length} curso${years.length === 1 ? '' : 's'} · ${subjects} ${subjects === 1 ? 'asignatura' : 'asignaturas'}`)}
                ${stat('Programadas', h(done), ideal ? `${Math.round(done / ideal * 100)} % del plan` : 'Sin horas previstas', 'is-teal')}
                ${stat('Por programar', h(missing), `en ${missingSubjects} ${missingSubjects === 1 ? 'asignatura' : 'asignaturas'}`, 'is-amber')}
                ${stat('Se pasan', over.length, over.length ? over[0] + (over.length > 1 ? ` y ${over.length - 1} más` : '') : 'Ninguna', over.length ? 'is-red' : '')}
            </div>
            <div class="pe-card">
                <div class="study-plan-table-wrapper">
                    <table class="pe-table">
                        <thead>
                            <tr><th scope="col">Asignatura</th>${years.map(y => `<th scope="col">${Utils.sanitizeHTML(y.name)} curso</th>`).join('')}<th scope="col">Total</th></tr>
                        </thead>
                        <tbody>
                            ${blocks.map(b => {
                                const bi = {}, bd = {};
                                b.subjects.forEach(s => years.forEach(y => { bi[y.id] = (bi[y.id] || 0) + (s.ideal[y.id] || 0); bd[y.id] = (bd[y.id] || 0) + (s.done[y.id] || 0); }));
                                return `
                                    <tr class="pe-block"><th colspan="${years.length + 2}" scope="rowgroup" style="--pe-color: ${color(b.color)};">${Utils.sanitizeHTML(this.blockLabel(b))}</th></tr>
                                    ${[...b.subjects.values()].map(s => `
                                        <tr>
                                            <th scope="row" class="pe-subject">${Utils.sanitizeHTML(s.name)}</th>
                                            ${years.map(y => cell(s.ideal[y.id] || 0, s.done[y.id] || 0)).join('')}
                                            ${cell(sum(s.ideal), sum(s.done))}
                                        </tr>`).join('')}
                                    <tr class="pe-subtotal">
                                        <th scope="row">Subtotal ${Utils.sanitizeHTML(this.blockLabel(b))}</th>
                                        ${years.map(y => sumCell(bi[y.id] || 0, bd[y.id] || 0)).join('')}
                                        ${sumCell(sum(bi), sum(bd))}
                                    </tr>`;
                            }).join('')}
                            <tr class="pe-total">
                                <th scope="row">Total del plan</th>
                                ${years.map(y => sumCell(perYear[y.id].ideal, perYear[y.id].done)).join('')}
                                ${sumCell(ideal, done)}
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
            <p class="pe-legend">Cada casilla: horas programadas / previstas. <span class="pe-key is-done">Completa</span> <span class="pe-key is-going">En curso</span> <span class="pe-key is-over">Se pasa</span></p>
        `;
    },

    switchStudyPlanTab(tab) {
        this.currentStudyPlanTab = tab;
        App.render();
    },

    switchStudyPlan(planId) {
        this.currentStudyPlanId = planId;
        App.render(); // the plan chips show which one is chosen
    },

    // Phone (formaciones.css): the three connected rows (view › type › plan) fold into one
    // line. Tapping a level unfolds its options; choosing one opens the next level.
    toggleStudyPlanLevel(level) {
        this._spOpenLevel = this._spOpenLevel === level ? null : level;
        App.render();
    },

    pickStudyPlanLevel(level, value) {
        if (level === 'tab') {
            this.currentStudyPlanTab = value;
            this._spOpenLevel = value === 'quadrant' || value === 'config' ? 'cat' : null;
            App.render();
        } else if (level === 'cat') {
            this._spOpenLevel = this.currentStudyPlanTab === 'quadrant' ? 'plan' : null;
            this.switchCategory(value);
        } else {
            this._spOpenLevel = null;
            this.currentStudyPlanId = value;
            App.render();
        }
    },

    renderStudyPlanCascade(categories, currentPlans) {
        const tabs = [['quadrant', 'Cuadrante', 'fa-table-cells'], ['config', 'Bloques', 'fa-sliders'], ['plans', 'Planes', 'fa-list-check'], ['types', 'Tipos', 'fa-shapes']];
        const tab = this.currentStudyPlanTab;
        const withCategory = tab === 'quadrant' || tab === 'config';
        const withPlan = tab === 'quadrant' && currentPlans.length > 0;
        const plan = currentPlans.find(p => String(p.id) === String(this.currentStudyPlanId));
        const open = this._spOpenLevel;
        const segment = (level, label) => `
            <button type="button" class="sp-cascade-seg ${open === level ? 'is-open' : ''}" aria-expanded="${open === level}"
                ${UIEvents.attr("click", function(event,captured){Formaciones.toggleStudyPlanLevel(String(captured[0]));}, [level])}>
                <span>${Utils.sanitizeHTML(label)}</span><i class="fas fa-chevron-down" aria-hidden="true"></i>
            </button>`;
        const options = level => {
            const list = level === 'tab' ? tabs.map(([id, label, icon]) => ({id, label, icon, active: id === tab}))
                : level === 'cat' ? categories.map(cat => ({id: cat, label: FormationTypeService.plural(cat), icon: FormationTypeService.icon(cat).replace(/[^a-z0-9-]/g, ''), active: cat === this.currentCategory}))
                : currentPlans.map(p => ({id: p.id, label: p.name, icon: 'fa-graduation-cap', active: String(p.id) === String(this.currentStudyPlanId)}));
            return `<div class="sp-cascade-options" role="listbox">${list.map(o => `
                <button type="button" role="option" aria-selected="${o.active}" class="sp-cascade-option ${o.active ? 'is-active' : ''}"
                    ${UIEvents.attr("click", function(event,captured){Formaciones.pickStudyPlanLevel(String(captured[0]), String(captured[1]));}, [level, o.id])}>
                    <i class="fas ${o.icon}" aria-hidden="true"></i><span>${Utils.sanitizeHTML(o.label)}</span>
                    ${o.active ? '<i class="fas fa-check sp-cascade-check" aria-hidden="true"></i>' : ''}
                </button>`).join('')}</div>`;
        };
        return `
            <nav class="sp-cascade" aria-label="Vista, tipo y plan">
                <div class="sp-cascade-bar">
                    ${segment('tab', (tabs.find(t => t[0] === tab) || tabs[0])[1])}
                    ${withCategory ? `<i class="fas fa-angle-right sp-cascade-sep" aria-hidden="true"></i>${segment('cat', FormationTypeService.plural(this.currentCategory))}` : ''}
                    ${withPlan ? `<i class="fas fa-angle-right sp-cascade-sep" aria-hidden="true"></i>${segment('plan', plan ? plan.name : 'Elige plan')}` : ''}
                </div>
                ${open === 'tab' || (open === 'cat' && withCategory) || (open === 'plan' && withPlan) ? options(open) : ''}
            </nav>`;
    },

    switchCategory(category) {
        this.currentCategory = category;
        this.currentStudyBlockId = null;
        const config = this.FORMATION_CONFIG[category];
        if (config && config.studyPlans.length > 0) {
            this.currentStudyPlanId = config.studyPlans[0].id;
        } else {
            this.currentStudyPlanId = null;
        }
        App.render();
    },

    renderPlanEstudios() {
        if (!this.currentCategory || !this.FORMATION_CONFIG[this.currentCategory]) this.currentCategory = FormationTypeService.firstId();
        if (!this.currentStudyPlanTab) this.currentStudyPlanTab = 'quadrant';

        const categories = Object.keys(this.FORMATION_CONFIG);
        const currentPlans = this.FORMATION_CONFIG[this.currentCategory]?.studyPlans || [];

        if (currentPlans.length > 0 && !currentPlans.find(p => p.id === this.currentStudyPlanId)) {
            this.currentStudyPlanId = currentPlans[0].id;
        }

        const tab = this.currentStudyPlanTab;
        const tabs = [['quadrant', 'Cuadrante'], ['config', 'Bloques'], ['plans', 'Planes'], ['types', 'Tipos']];
        const subtitle = {
            quadrant: 'Horas previstas de cada asignatura y las ya programadas este curso',
            config: 'Bloques académicos de cada tipo y sus asignaturas',
            plans: 'Planes de estudios de la escuela',
            types: 'Tipos de formación: nombre, color y cursos'
        }[tab] || '';
        const chip = (on, label, handler, value) => `
            <button type="button" class="pe-chip ${on ? 'is-on' : ''}" aria-pressed="${on}" ${UIEvents.attr("click", handler, [value])}>${Utils.sanitizeHTML(label)}</button>`;
        const blockData = tab === 'config' ? this.studyBlockData(this.currentCategory) : null;
        const content = tab === 'types' ? this.renderFormationTypesManager()
            : tab === 'plans' ? this.renderStudyPlansManager()
            : tab === 'config' ? this.renderStudyPlanConfig(this.currentCategory, blockData)
            : this.renderStudyPlan(this.currentStudyPlanId);

        return `
            <div class="dnr-proposal pe-view">
                <header class="pe-head">
                    <div>
                        <h1 class="pe-title">Plan de estudios</h1>
                        <p class="pe-sub">${subtitle}</p>
                    </div>
                    <div class="sp-mode-tabs pe-tabs" role="tablist" aria-label="Secciones">
                        ${tabs.map(([id, label]) => `
                            <button type="button" role="tab" class="pe-tab ${tab === id ? 'is-on' : ''}" aria-selected="${tab === id}"
                                ${UIEvents.attr("click", function(event,captured){Formaciones.switchStudyPlanTab(String(captured[0]));}, [id])}>${label}</button>`).join('')}
                    </div>
                </header>

                ${this.renderStudyPlanCascade(categories, currentPlans)}

                ${tab === 'quadrant' || tab === 'config' ? `
                <div class="pe-bar">
                    <div class="sp-cat-pills pe-group" role="group" aria-label="Tipo de formación">
                        <span class="pe-bar-label">Tipo</span>
                        ${categories.map(cat => chip(this.currentCategory === cat, FormationTypeService.plural(cat), function(event,captured){Formaciones.switchCategory(String(captured[0]));}, cat)).join('')}
                    </div>
                    ${blockData ? `<span class="pe-bar-info">${blockData.formations.length} formaciones · ${blockData.subjects.length} asignaturas</span>` : ''}
                    ${tab === 'quadrant' && currentPlans.length ? `
                    <span class="pe-bar-sep" aria-hidden="true"></span>
                    <div class="sp-plan-tabs pe-group" role="group" aria-label="Plan de estudios">
                        <span class="pe-bar-label">Plan</span>
                        ${currentPlans.map(plan => chip(String(this.currentStudyPlanId) === String(plan.id), plan.name, function(event,captured){Formaciones.switchStudyPlan(String(captured[0]));}, plan.id)).join('')}
                    </div>` : ''}
                </div>` : ''}

                <div id="study-plan-content">
                    ${content}
                </div>
            </div>
        `;
    },

    // Tipos, diseño A (2026-10-26): el mismo aspecto que Planes (.spl-*). Cada tipo con su icono en su
    // color, nombre / plural, cursos, opciones activas, formaciones y planes, estado y acciones.
    renderFormationTypesManager() {
        const manage = this.canManagePlans();
        const formations = DataManager.getFormations();
        const types = FormationTypeService.getTypes();
        const esc = value => Utils.sanitizeHTML(String(value));
        const rows = types.map((type, index) => {
            const count = formations.filter(f => f.type === type.id).length;
            const plans = StudyPlanService.getPlans().filter(p => p.category === type.id).length;
            const color = /^#[0-9a-f]{6}$/i.test(type.color) ? type.color : '#64748b';
            const flags = [type.ranksPreferences ? 'Ordena preferencias' : '', type.warehouseAccess ? 'Acceso al almacén' : ''].filter(Boolean);
            return `
                <li class="spl-row ${type.active ? '' : 'is-inactive'}" data-type-id="${esc(type.id)}">
                    ${manage ? `<span class="spl-order">
                        <button type="button" class="spl-icon is-small" aria-label="Subir ${esc(type.name)}" title="Subir" ${index === 0 ? 'disabled' : ''} ${UIEvents.attr("click", function(event,captured){Formaciones.moveType(String(captured[0]),-1);}, [type.id])}><i class="fas fa-chevron-up"></i></button>
                        <button type="button" class="spl-icon is-small" aria-label="Bajar ${esc(type.name)}" title="Bajar" ${index === types.length - 1 ? 'disabled' : ''} ${UIEvents.attr("click", function(event,captured){Formaciones.moveType(String(captured[0]),1);}, [type.id])}><i class="fas fa-chevron-down"></i></button>
                    </span>` : ''}
                    <span class="spl-badge is-icon" style="--spl-color: ${color};" aria-hidden="true"><i class="fas ${String(type.icon).replace(/[^a-z0-9-]/g, '')}"></i></span>
                    <span class="spl-main">
                        <span><b class="spl-name">${esc(type.name)}</b> <span class="spl-plural">/ ${esc(type.pluralName)}</span></span>
                        <span class="spl-years">
                            ${(type.years || []).map(y => `<span class="spl-year">${esc(y.name)}</span>`).join('')}
                            ${flags.map(flag => `<span class="spl-flag">${flag}</span>`).join('')}
                        </span>
                    </span>
                    <span class="spl-forms is-wide">${count} ${count === 1 ? 'formación' : 'formaciones'} · ${plans} ${plans === 1 ? 'plan' : 'planes'}</span>
                    <span class="spl-state ${type.active ? 'is-active' : ''}">${type.active ? 'Activo' : 'Desactivado'}</span>
                    ${manage ? `<span class="spl-actions">
                        <button type="button" class="spl-icon" title="Editar" aria-label="Editar ${esc(type.name)}" ${UIEvents.attr("click", function(event,captured){Formaciones.openTypeForm(String(captured[0]));}, [type.id])}><i class="fas fa-pen"></i></button>
                        <button type="button" class="spl-icon" title="${type.active ? 'Desactivar' : 'Activar'}" aria-label="${type.active ? 'Desactivar' : 'Activar'} ${esc(type.name)}" ${UIEvents.attr("click", function(event,captured){Formaciones.toggleType(String(captured[0]));}, [type.id])}><i class="fas ${type.active ? 'fa-eye-slash' : 'fa-eye'}"></i></button>
                        <button type="button" class="spl-icon is-danger" title="Eliminar" aria-label="Eliminar ${esc(type.name)}" ${UIEvents.attr("click", function(event,captured){Formaciones.deleteType(String(captured[0]));}, [type.id])}><i class="fas fa-trash-can"></i></button>
                    </span>` : ''}
                </li>`;
        }).join('');
        return `
            <div class="spl-manager">
                <div class="spl-head">
                    <p class="spl-help">Los tipos clasifican las formaciones (por ejemplo Diplomatura, Máster, Curso). Cada tipo tiene sus cursos (1º, 2º, 3º o Único), su icono y su color. Cambiar el nombre no rompe nada; un tipo en uso no se borra, se desactiva.</p>
                    ${manage ? `<button type="button" class="spl-btn is-primary" ${UIEvents.attr("click", function(event,captured){Formaciones.openTypeForm('');}, [])}><i class="fas fa-plus"></i> Nuevo tipo</button>` : ''}
                </div>
                <section class="spl-card">
                    <header class="spl-card-head">
                        <h2 class="spl-h2">Tipos de formación</h2>
                        <span class="spl-count">${types.length}</span>
                        <span class="spl-type-years">El orden de esta lista es el de los botones de tipo en toda la app</span>
                    </header>
                    ${types.length ? `<ul class="spl-list">${rows}</ul>` : '<p class="spl-empty">Todavía no hay tipos. Crea el primero para poder dar de alta formaciones.</p>'}
                </section>
            </div>`;
    },

    // One editable course row (type or plan form); `id` is kept so formations stay linked on rename.
    typeYearRow(year) {
        return `
            <div class="ft-year-row" data-year-id="${Utils.sanitizeHTML(year.id)}">
                <input type="text" class="form-input" maxlength="40" required value="${Utils.sanitizeHTML(year.name)}" aria-label="Nombre del curso">
                <button type="button" class="btn-icon" title="Quitar curso" aria-label="Quitar curso" ${UIEvents.attr("click", function(event,captured){Formaciones.removeTypeYear(this);}, [])}><i class="fas fa-times"></i></button>
            </div>`;
    },

    readTypeYears(form) {
        return [...form.querySelectorAll('.ft-year-row')].map(row => ({id: row.dataset.yearId, name: row.querySelector('input').value}));
    },

    addTypeYear(button) {
        const form = button.closest('form');
        const years = this.readTypeYears(form);
        if (years.length >= 10) { Utils.showToast('Como máximo 10 cursos.', 'error'); return; }
        // Next free numeric id ("1", "2"…), so new courses never reuse an id in use.
        let next = years.length + 1;
        while (years.some(y => y.id === String(next))) next++;
        years.push({id: String(next), name: `${next}º`});
        SafeHTML.set(form.querySelector('.ft-years-editor'), years.map(y => this.typeYearRow(y)).join(''));
    },

    removeTypeYear(button) {
        const form = button.closest('form');
        const row = button.closest('.ft-year-row');
        const {typeId, planId} = form.dataset;
        const years = this.readTypeYears(form);
        if (years.length <= 1) { Utils.showToast('Hace falta al menos un curso.', 'error'); return; }
        // A course a formation uses cannot be removed (it can be renamed).
        const owner = f => typeId ? f.type === typeId : planId ? f.studyPlanId === planId : false;
        const used = DataManager.getFormations().filter(f => owner(f) && String(f.academicYear) === row.dataset.yearId).length;
        if (used) { Utils.showToast(`No se puede quitar: lo usan ${used} ${used === 1 ? 'formación' : 'formaciones'}. Puedes cambiarle el nombre.`, 'error'); return; }
        row.remove();
    },

    openTypeForm(id) {
        if (!this.canManagePlans()) return;
        const type = id ? FormationTypeService.getType(id) : null;
        if (id && !type) return;
        const years = type?.years?.length ? type.years : [{id: '1', name: 'Único'}];
        const icon = type?.icon || FormationTypeService.ICONS[0];
        const content = `
            <div class="dnr-proposal">
                <form id="formation-type-form" data-type-id="${Utils.sanitizeHTML(id || '')}" ${UIEvents.attr("submit", function(event,captured){Formaciones.saveType(event,String(captured[0]));}, [id || ''])}>
                    <div class="ft-form-grid">
                        <div class="form-group">
                            <label class="form-label" for="ft-name">Nombre *</label>
                            <input id="ft-name" type="text" name="name" class="form-input" required maxlength="60" value="${Utils.sanitizeHTML(type?.name || '')}" placeholder="Ej: Diplomatura">
                        </div>
                        <div class="form-group">
                            <label class="form-label" for="ft-plural">Plural *</label>
                            <input id="ft-plural" type="text" name="pluralName" class="form-input" required maxlength="60" value="${Utils.sanitizeHTML(type?.pluralName || '')}" placeholder="Ej: Diplomaturas">
                        </div>
                    </div>
                    <div class="form-group">
                        <span class="form-label">Icono</span>
                        <div class="ft-icon-picker" role="radiogroup" aria-label="Icono">
                            ${FormationTypeService.ICONS.map(name => `
                                <label class="ft-icon-option" title="${name}">
                                    <input type="radio" name="icon" value="${name}" ${name === icon ? 'checked' : ''}>
                                    <i class="fas ${name}"></i>
                                </label>`).join('')}
                        </div>
                    </div>
                    <div class="form-group">
                        <label class="form-label" for="ft-color">Color</label>
                        <input id="ft-color" type="color" name="color" class="ft-color" value="${/^#[0-9a-f]{6}$/i.test(type?.color || '') ? type.color : '#0f766e'}">
                    </div>
                    <div class="form-group">
                        <span class="form-label">Cursos *</span>
                        <p class="ft-hint">Los cursos que puede tener una formación de este tipo, en orden (por ejemplo 1º, 2º, 3º; o Único).</p>
                        <div class="ft-years-editor">${years.map(y => this.typeYearRow(y)).join('')}</div>
                        <button type="button" class="btn btn-secondary btn-sm" ${UIEvents.attr("click", function(event,captured){Formaciones.addTypeYear(this);}, [])}><i class="fas fa-plus"></i> Añadir curso</button>
                    </div>
                    <label class="form-check ft-check">
                        <input type="checkbox" name="ranksPreferences" ${type?.ranksPreferences ? 'checked' : ''}>
                        <span><strong>El solicitante ordena sus preferencias</strong><br><small>En el formulario de inscripción, en vez de elegir una formación, ordena todas las de este tipo.</small></span>
                    </label>
                    <label class="form-check ft-check">
                        <input type="checkbox" name="warehouseAccess" ${type?.warehouseAccess ? 'checked' : ''}>
                        <span><strong>Da acceso al almacén</strong><br><small>Los alumnos de formaciones de este tipo pueden reservar material.</small></span>
                    </label>
                    ${type ? `<label class="form-check ft-check">
                        <input type="checkbox" name="active" ${type.active ? 'checked' : ''}>
                        <span><strong>Activo</strong><br><small>Sale en las listas al crear formaciones y planes, y en el formulario de inscripción.</small></span>
                    </label>` : ''}
                    <div class="modal-sticky-actions">
                        <button type="button" class="btn btn-secondary" ${UIEvents.attr("click", function(event,captured){Utils.closeModal();}, [])}>Cancelar</button>
                        <button type="submit" class="btn btn-primary">${type ? 'Guardar cambios' : 'Crear tipo'}</button>
                    </div>
                </form>
            </div>`;
        Utils.openModal(type ? 'Editar tipo de formación' : 'Nuevo tipo de formación', content);
    },

    async saveType(event, id) {
        event.preventDefault();
        if (!this.canManagePlans() || this._savingType) return;
        const form = event.target;
        if (!form.checkValidity()) { form.reportValidity(); return; }
        const data = {
            name: form.elements.name.value, pluralName: form.elements.pluralName.value,
            icon: form.querySelector('input[name="icon"]:checked')?.value, color: form.elements.color.value,
            years: this.readTypeYears(form),
            ranksPreferences: form.elements.ranksPreferences.checked, warehouseAccess: form.elements.warehouseAccess.checked
        };
        if (id) data.active = !!form.elements.active?.checked;
        this._savingType = true;
        form.querySelectorAll('button').forEach(button => { button.disabled = true; });
        try {
            if (id) await FormationTypeService.update(id, data); else await FormationTypeService.create(data);
            Utils.closeModal();
            Utils.showToast(id ? 'Tipo actualizado.' : 'Tipo creado.', 'success');
            App.render();
        } catch (error) {
            Utils.showToast(error.message, 'error');
            form.querySelectorAll('button').forEach(button => { button.disabled = false; });
        } finally {
            this._savingType = false;
        }
    },

    async moveType(id, step) {
        if (!this.canManagePlans() || this._savingType) return;
        const types = FormationTypeService.getTypes();
        const index = types.findIndex(t => t.id === id), target = index + step;
        if (index < 0 || target < 0 || target >= types.length) return;
        [types[index], types[target]] = [types[target], types[index]];
        this._savingType = true;
        try {
            // Renumber all so equal or missing orders become a clean sequence.
            for (const [order, type] of types.entries()) {
                if (type.order !== order) await FormationTypeService.update(type.id, {order});
            }
            App.render();
        } catch (error) {
            Utils.showToast(error.message, 'error');
        } finally {
            this._savingType = false;
        }
    },

    async toggleType(id) {
        const type = FormationTypeService.getType(id);
        if (!type || !this.canManagePlans()) return;
        try {
            await FormationTypeService.update(id, {active: !type.active});
            Utils.showToast(type.active ? 'Tipo desactivado: ya no sale en las listas, pero lo que lo usaba lo conserva.' : 'Tipo activado.', 'success');
            App.render();
        } catch (error) {
            Utils.showToast(error.message, 'error');
        }
    },

    async deleteType(id) {
        const type = FormationTypeService.getType(id);
        if (!type || !this.canManagePlans()) return;
        const answer = await SafeHTML.fire({
            icon: 'warning', title: '¿Eliminar el tipo?', text: `«${type.name}» se eliminará. Solo es posible si ninguna formación ni plan de estudios lo usa.`,
            showCancelButton: true, confirmButtonText: 'Eliminar', cancelButtonText: 'Cancelar'
        });
        if (!answer?.isConfirmed) return;
        try {
            await FormationTypeService.remove(id);
            Utils.showToast('Tipo eliminado.', 'success');
            App.render();
        } catch (error) {
            Utils.showToast(error.message, 'error');
        }
    },

    // --- Planes: the school's study plans (create, rename, type, order, deactivate, delete) ---
    canManagePlans() {
        return AuthManager.canManageModule('plan-estudios', ['admin', 'coordinator']);
    },

    // Planes, diseño A (2026-10-26): una tarjeta por tipo y, en cada plan, sus iniciales en su color,
    // sus cursos (del tipo o propios), cuántas formaciones lo usan y su estado.
    // Estilos: assets/css/views/formaciones.css (.spl-*); la pestaña Tipos sigue con .sp-*.
    renderStudyPlansManager() {
        const manage = this.canManagePlans();
        const formations = DataManager.getFormations();
        const esc = value => Utils.sanitizeHTML(String(value));
        const sections = StudyPlanService.CATEGORIES.filter(category => category.active || StudyPlanService.getPlans().some(p => p.category === category.id)).map(category => {
            const plans = StudyPlanService.getPlans().filter(p => p.category === category.id)
                .sort((a, b) => a.order - b.order || String(a.name).localeCompare(String(b.name), 'es'));
            const typeYears = FormationTypeService.years(category.id);
            const rows = plans.map((plan, index) => {
                const count = formations.filter(f => f.studyPlanId === plan.id).length;
                const own = Array.isArray(plan.years) && plan.years.length > 0;
                const years = own ? plan.years : typeYears;
                const color = /^#[0-9a-f]{6}$/i.test(plan.color || '') ? plan.color : '#475569';
                const initials = plan.initials || String(plan.name || '?').trim().charAt(0).toUpperCase();
                return `
                    <li class="spl-row ${plan.active ? '' : 'is-inactive'}" data-plan-id="${esc(plan.id)}">
                        ${manage ? `<span class="spl-order">
                            <button type="button" class="spl-icon is-small" aria-label="Subir ${esc(plan.name)}" title="Subir" ${index === 0 ? 'disabled' : ''} ${UIEvents.attr("click", function(event,captured){Formaciones.movePlan(String(captured[0]),-1);}, [plan.id])}><i class="fas fa-chevron-up"></i></button>
                            <button type="button" class="spl-icon is-small" aria-label="Bajar ${esc(plan.name)}" title="Bajar" ${index === plans.length - 1 ? 'disabled' : ''} ${UIEvents.attr("click", function(event,captured){Formaciones.movePlan(String(captured[0]),1);}, [plan.id])}><i class="fas fa-chevron-down"></i></button>
                        </span>` : ''}
                        <span class="spl-badge" style="--spl-color: ${color};" aria-hidden="true">${esc(initials)}</span>
                        <span class="spl-main">
                            <b class="spl-name">${esc(plan.name)}</b>
                            <span class="spl-years">
                                ${years.map(y => `<span class="spl-year">${esc(y.name)}</span>`).join('')}
                                <span class="spl-own ${own ? 'is-own' : ''}">${own ? 'Cursos propios' : 'Como el tipo'}</span>
                            </span>
                        </span>
                        <span class="spl-forms">${count} ${count === 1 ? 'formación' : 'formaciones'}</span>
                        <span class="spl-state ${plan.active ? 'is-active' : ''}">${plan.active ? 'Activo' : 'Desactivado'}</span>
                        ${manage ? `<span class="spl-actions">
                            <button type="button" class="spl-icon" title="Editar" aria-label="Editar ${esc(plan.name)}" ${UIEvents.attr("click", function(event,captured){Formaciones.openPlanForm(String(captured[0]));}, [plan.id])}><i class="fas fa-pen"></i></button>
                            <button type="button" class="spl-icon" title="${plan.active ? 'Desactivar' : 'Activar'}" aria-label="${plan.active ? 'Desactivar' : 'Activar'} ${esc(plan.name)}" ${UIEvents.attr("click", function(event,captured){Formaciones.togglePlan(String(captured[0]));}, [plan.id])}><i class="fas ${plan.active ? 'fa-eye-slash' : 'fa-eye'}"></i></button>
                            <button type="button" class="spl-icon is-danger" title="Eliminar" aria-label="Eliminar ${esc(plan.name)}" ${UIEvents.attr("click", function(event,captured){Formaciones.deletePlan(String(captured[0]));}, [plan.id])}><i class="fas fa-trash-can"></i></button>
                        </span>` : ''}
                    </li>`;
            }).join('');
            return `
                <section class="spl-card">
                    <header class="spl-card-head">
                        <h2 class="spl-h2">${esc(category.label)}</h2>
                        <span class="spl-count">${plans.length}</span>
                        ${typeYears.length ? `<span class="spl-type-years">Cursos del tipo: ${typeYears.map(y => esc(y.name)).join(' · ')}</span>` : ''}
                    </header>
                    ${plans.length ? `<ul class="spl-list">${rows}</ul>` : '<p class="spl-empty">Sin planes de este tipo.</p>'}
                </section>`;
        }).join('');
        return `
            <div class="spl-manager">
                <div class="spl-head">
                    <p class="spl-help">Un plan agrupa las formaciones de cada año (por ejemplo, Dirección cinematográfica = Dirección 1º, 2º y 3º). Se usa al crear formaciones y en Contactos. Cambiar el nombre no rompe nada; un plan en uso no se borra, se desactiva.</p>
                    ${manage ? `<button type="button" class="spl-btn is-primary" ${UIEvents.attr("click", function(event,captured){Formaciones.openPlanForm('');}, [])}><i class="fas fa-plus"></i> Nuevo plan</button>` : ''}
                </div>
                <div class="spl-grid">${sections}</div>
            </div>`;
    },

    openPlanForm(id) {
        if (!this.canManagePlans()) return;
        const plan = id ? StudyPlanService.getPlan(id) : null;
        if (id && !plan) return;
        const category = plan?.category || this.currentCategory || FormationTypeService.firstId();
        const ownYears = Array.isArray(plan?.years) && plan.years.length > 0;
        const years = ownYears ? plan.years : (FormationTypeService.years(category).length ? FormationTypeService.years(category) : [{id: '1', name: 'Único'}]);
        const content = `
            <div class="dnr-proposal">
                <form id="study-plan-form" data-plan-id="${Utils.sanitizeHTML(id || '')}" data-category="${Utils.sanitizeHTML(category)}" ${UIEvents.attr("submit", function(event,captured){Formaciones.savePlan(event,String(captured[0]));}, [id || ''])}>
                    <div class="form-group">
                        <label class="form-label" for="study-plan-name">Nombre del plan *</label>
                        <input id="study-plan-name" type="text" name="name" class="form-input" required maxlength="120" value="${Utils.sanitizeHTML(plan?.name || '')}" placeholder="Ej: Dirección Cinematográfica">
                    </div>
                    <div class="form-group">
                        <label class="form-label" for="study-plan-category">Tipo de formación *</label>
                        <select id="study-plan-category" name="category" class="form-select" required ${UIEvents.attr("change", function(event,captured){Formaciones.changePlanType(this.form);}, [])}>
                            ${StudyPlanService.CATEGORIES.filter(c => c.active || c.id === plan?.category).map(c => `<option value="${Utils.sanitizeHTML(c.id)}" ${c.id === category ? 'selected' : ''}>${Utils.sanitizeHTML(c.single)}${c.active ? '' : ' (desactivado)'}</option>`).join('')}
                        </select>
                    </div>
                    <div class="ft-form-grid">
                        <div class="form-group">
                            <label class="form-label" for="study-plan-initials">Iniciales</label>
                            <input id="study-plan-initials" type="text" name="initials" class="form-input" maxlength="3" pattern="[\\p{L}\\p{N}]{1,3}" value="${Utils.sanitizeHTML(plan?.initials || '')}" placeholder="Ej: D">
                        </div>
                        <div class="form-group">
                            <label class="form-label" for="study-plan-color">Color</label>
                            <input id="study-plan-color" type="color" name="color" class="ft-color" value="${/^#[0-9a-f]{6}$/i.test(plan?.color || '') ? plan.color : '#475569'}">
                        </div>
                    </div>
                    <p class="ft-hint" style="margin-top: -0.5rem;">Letra y color con que se distingue el plan cuando se juntan varios (por ejemplo, en un grupo de Pre-Matrícula).</p>
                    <fieldset class="form-group sp-own-years">
                        <legend class="form-label">Cursos *</legend>
                        <p class="ft-hint">Los cursos de este plan, en orden. Si los dejas como los de su tipo, el plan sigue los cambios que hagas en el tipo.</p>
                        <div class="ft-years-editor">${years.map(y => this.typeYearRow(y)).join('')}</div>
                        <button type="button" class="btn btn-secondary btn-sm" ${UIEvents.attr("click", function(event,captured){Formaciones.addTypeYear(this);}, [])}><i class="fas fa-plus"></i> Añadir curso</button>
                    </fieldset>
                    ${plan ? `<label class="form-check" style="display: flex; gap: 0.5rem; align-items: center; margin-bottom: 1rem;">
                        <input type="checkbox" name="active" ${plan.active ? 'checked' : ''}> Activo (sale en las listas para formaciones y Contactos CRM)
                    </label>` : ''}
                    <div class="modal-sticky-actions">
                        <button type="button" class="btn btn-secondary" ${UIEvents.attr("click", function(event,captured){Utils.closeModal();}, [])}>Cancelar</button>
                        <button type="submit" class="btn btn-primary">${plan ? 'Guardar cambios' : 'Crear plan'}</button>
                    </div>
                </form>
            </div>`;
        Utils.openModal(plan ? 'Editar plan de estudios' : 'Nuevo plan de estudios', content);
    },

    async savePlan(event, id) {
        event.preventDefault();
        if (!this.canManagePlans() || this._savingPlan) return;
        const form = event.target;
        if (!form.checkValidity()) { form.reportValidity(); return; }
        const data = {name: form.elements.name.value, category: form.elements.category.value,
            // Same courses as its type: keep following the type (null); otherwise its own.
            years: this.sameYears(this.readTypeYears(form), FormationTypeService.years(form.elements.category.value)) ? null : this.readTypeYears(form),
            initials: form.elements.initials.value.trim() || null, color: form.elements.color.value};
        if (id) data.active = !!form.elements.active?.checked;
        this._savingPlan = true;
        form.querySelectorAll('button').forEach(button => { button.disabled = true; });
        try {
            if (id) await StudyPlanService.update(id, data); else await StudyPlanService.create(data);
            Utils.closeModal();
            Utils.showToast(id ? 'Plan actualizado.' : 'Plan creado.', 'success');
            App.render();
        } catch (error) {
            Utils.showToast(error.message, 'error');
            form.querySelectorAll('button').forEach(button => { button.disabled = false; });
        } finally {
            this._savingPlan = false;
        }
    },

    sameYears(a, b) {
        return a.length === b.length && a.every((year, i) => String(year.id) === String(b[i].id) && year.name.trim() === b[i].name.trim());
    },

    // Changing the plan's type: courses not touched yet follow the new type.
    changePlanType(form) {
        const previous = FormationTypeService.years(form.dataset.category);
        const next = FormationTypeService.years(form.elements.category.value);
        if (next.length && this.sameYears(this.readTypeYears(form), previous)) {
            SafeHTML.set(form.querySelector('.ft-years-editor'), next.map(y => this.typeYearRow(y)).join(''));
        }
        form.dataset.category = form.elements.category.value;
    },

    async movePlan(id, step) {
        const plan = StudyPlanService.getPlan(id);
        if (!plan || !this.canManagePlans() || this._savingPlan) return;
        const siblings = StudyPlanService.getPlans().filter(p => p.category === plan.category)
            .sort((a, b) => a.order - b.order || String(a.name).localeCompare(String(b.name), 'es'));
        const index = siblings.findIndex(p => p.id === id), target = index + step;
        if (target < 0 || target >= siblings.length) return;
        [siblings[index], siblings[target]] = [siblings[target], siblings[index]];
        this._savingPlan = true;
        try {
            // Renumber the whole group so equal or missing orders become a clean sequence.
            for (const [order, sibling] of siblings.entries()) {
                if (sibling.order !== order) await StudyPlanService.update(sibling.id, {order});
            }
            App.render();
        } catch (error) {
            Utils.showToast(error.message, 'error');
        } finally {
            this._savingPlan = false;
        }
    },

    async togglePlan(id) {
        const plan = StudyPlanService.getPlan(id);
        if (!plan || !this.canManagePlans()) return;
        try {
            await StudyPlanService.update(id, {active: !plan.active});
            Utils.showToast(plan.active ? 'Plan desactivado: ya no sale en las listas, pero lo que lo usaba lo conserva.' : 'Plan activado.', 'success');
            App.render();
        } catch (error) {
            Utils.showToast(error.message, 'error');
        }
    },

    async deletePlan(id) {
        const plan = StudyPlanService.getPlan(id);
        if (!plan || !this.canManagePlans()) return;
        const answer = await SafeHTML.fire({
            icon: 'warning', title: '¿Eliminar el plan?', text: `«${plan.name}» se eliminará. Solo es posible si ninguna formación, regla del CRM ni contacto lo usa.`,
            showCancelButton: true, confirmButtonText: 'Eliminar', cancelButtonText: 'Cancelar'
        });
        if (!answer?.isConfirmed) return;
        try {
            await StudyPlanService.remove(id);
            Utils.showToast('Plan eliminado.', 'success');
            App.render();
        } catch (error) {
            Utils.showToast(error.message, 'error');
        }
    },

    // Bloques, diseño B (2026-10-26): los bloques a la izquierda («Sin bloque» primero) y el elegido a la
    // derecha con sus cifras y una tabla de asignaturas con las horas de cada curso.
    // Estilos: assets/css/views/formaciones.css (.blk-*).
    // OTROS is where the app puts subjects nobody classified (default on create, and where a deleted block's
    // subjects go), so in this tab it is not a block of its own: those subjects are listed under «Sin bloque».
    studyBlockData(category) {
        const blocks = this.getBlocksForCategory(category).filter(b => b.id !== 'OTROS');
        const formations = DataManager.getFormations().filter(f => (f.type || '').toLowerCase() === String(category).toLowerCase());
        const formationsMap = new Map(formations.map(f => [String(f.id), f]));
        const subjects = DataManager.getAll(DataManager.KEYS.SUBJECTS)
            .filter(s => formationsMap.has(String(s.formationId)) && !['VAC', 'EXT'].includes(s.type?.toUpperCase()));
        const yearOf = s => String(formationsMap.get(String(s.formationId)).academicYear || s.academicYear || '1');
        const typeYears = FormationTypeService.years(category).map(y => ({ id: String(y.id), name: y.name }));
        const years = [...typeYears];
        subjects.forEach(s => { if (!years.some(y => y.id === yearOf(s))) years.push({ id: yearOf(s), name: `${yearOf(s)}º` }); });
        if (!typeYears.length) years.sort((a, b) => a.id.localeCompare(b.id));

        // One row per subject name (accent and case insensitive); per course, the first subject holds the hours.
        const rows = list => {
            const groups = new Map();
            list.forEach(s => {
                const name = String(s.name || '').trim();
                const key = Utils.normalizeString(name);
                if (!groups.has(key)) groups.set(key, { key, name, byYear: new Map(), forms: new Set() });
                const group = groups.get(key);
                if (/[áéíóúñ]/i.test(name) && !/[áéíóúñ]/i.test(group.name)) group.name = name;
                if (!group.byYear.has(yearOf(s))) group.byYear.set(yearOf(s), s);
                const form = formationsMap.get(String(s.formationId));
                if (form?.name) group.forms.add(form.name);
            });
            return [...groups.values()].map(group => {
                const cells = years.map(y => group.byYear.get(y.id) || null);
                return { ...group, cells, total: cells.reduce((sum, s) => sum + (s ? parseFloat(s.totalHours) || 0 : 0), 0) };
            }).sort((a, b) => a.name.localeCompare(b.name, 'es'));
        };
        const ids = new Set(blocks.map(b => b.id));
        const lists = [{ id: '', name: 'Sin bloque', color: '#B86A1E', loose: true, subjects: subjects.filter(s => !ids.has(s.studyBlock)) }]
            .concat(blocks.map(b => ({ ...b, subjects: subjects.filter(s => s.studyBlock === b.id) })))
            .map(list => {
                const listRows = rows(list.subjects);
                return { ...list, rows: listRows, hours: listRows.reduce((sum, r) => sum + r.total, 0),
                    perYear: years.map((y, i) => listRows.reduce((sum, r) => sum + (r.cells[i] ? parseFloat(r.cells[i].totalHours) || 0 : 0), 0)) };
            });
        return { blocks, formations, subjects, years, lists };
    },

    pickStudyBlock(id) {
        this.currentStudyBlockId = id;
        App.render();
    },

    renderStudyPlanConfig(category, data = this.studyBlockData(category)) {
        const { blocks, years, lists } = data;
        const loose = lists[0];
        if (!lists.some(l => l.id === this.currentStudyBlockId)) this.currentStudyBlockId = loose.rows.length || lists.length === 1 ? '' : lists[1].id;
        const current = lists.find(l => l.id === this.currentStudyBlockId);
        const hex = color => /^#[0-9a-f]{3,8}$/i.test(color || '') ? color : '#64748b';
        const max = Math.max(1, ...lists.slice(1).map(l => l.hours));
        const esc = value => Utils.sanitizeHTML(String(value));

        const nav = lists.map(list => `
            <button type="button" class="blk-nav-item ${list.loose ? 'is-loose' : ''} ${list === current ? 'is-on' : ''}" aria-pressed="${list === current}" style="--blk-color: ${hex(list.color)}"
                ${UIEvents.attr("click", function(event,captured){Formaciones.pickStudyBlock(String(captured[0]));}, [list.id])}>
                <span class="blk-dot" aria-hidden="true"></span>
                <span class="blk-nav-body">
                    <span class="blk-nav-top"><span class="blk-nav-name">${esc(list.name)}</span><span class="blk-count">${list.rows.length}</span></span>
                    ${list.loose ? '' : `<span class="blk-share" aria-hidden="true"><i style="width: ${Math.round(list.hours / max * 100)}%"></i></span>`}
                    <span class="blk-nav-sub" data-pb-hours="${esc(list.id)}">${list.loose ? 'No salen en el cuadrante' : `${list.hours} h`}</span>
                </span>
            </button>`).join('');

        const others = blocks.filter(b => b.id !== current.id);
        const row = r => `
            <tr>
                <th scope="row" class="blk-subject">${esc(r.name)}</th>
                ${r.cells.map((s, i) => `<td>${s ? `<input type="number" class="blk-hours-input" min="0" max="999" step="1" data-subject-id="${esc(s.id)}"
                    value="${parseFloat(s.totalHours) || 0}" aria-label="Horas de ${esc(r.name)} en ${esc(years[i].name)}" title="Escribe las horas y pulsa Intro"
                    ${UIEvents.attr("focus", function(event,captured){this.select();}, [])}
                    ${UIEvents.attr("change", function(event,captured){Formaciones.updateSubjectHours(String(captured[0]),this.value,String(captured[1]),String(captured[2]));}, [s.id, category, current.id])}
                    ${UIEvents.attr("keydown", function(event,captured){if(event.key==='Enter')this.blur();}, [])}>` : '<span class="pe-none">—</span>'}</td>`).join('')}
                <td class="pe-n" data-pb-total="${esc(r.key)}">${r.total} h</td>
                <td class="blk-forms" title="${esc([...r.forms].join(', '))}">${esc([...r.forms].join(' · ') || '—')}</td>
                <td>
                    <div class="blk-move">
                        <select class="blk-select" aria-label="${current.loose ? 'Asignar' : 'Mover'} ${esc(r.name)} a otro bloque"
                            ${UIEvents.attr("change", function(event,captured){Formaciones.moveSubjectGroupToBlock(String(captured[0]),this.value,String(captured[1]));}, [r.key, category])}>
                            <option value="" selected disabled>${current.loose ? 'Asignar a…' : 'Mover a…'}</option>
                            ${others.map(b => `<option value="${esc(b.id)}">${esc(b.name)}</option>`).join('')}
                        </select>
                        ${current.loose ? '' : `<button type="button" class="btn-icon" aria-label="Quitar ${esc(r.name)} del bloque" title="Quitar del bloque"
                            ${UIEvents.attr("click", function(event,captured){Formaciones.moveSubjectGroupToBlock(String(captured[0]),'OTROS',String(captured[1]));}, [r.key, category])}><i class="fas fa-xmark"></i></button>`}
                    </div>
                </td>
            </tr>`;

        return `
            <div class="blk-layout">
                <nav class="blk-nav" aria-label="Bloques">
                    ${nav}
                    <button type="button" class="blk-new" ${UIEvents.attr("click", function(event,captured){Formaciones.openCreateBlockModal(String(captured[0]));}, [category])}><i class="fas fa-plus"></i> Nuevo bloque</button>
                </nav>
                <section class="blk-panel pe-card" style="--blk-color: ${hex(current.color)}">
                    <header class="blk-head">
                        <span class="blk-dot is-big" aria-hidden="true"></span>
                        <h2 class="blk-title">${esc(current.name)}</h2>
                        ${current.loose ? '<span class="blk-note">Asígnalas a un bloque para que salgan en el cuadrante</span>' : `
                        <button type="button" class="blk-btn" ${UIEvents.attr("click", function(event,captured){Formaciones.openEditBlockModal(String(captured[0]),String(captured[1]));}, [category, current.id])}>Editar nombre o color</button>
                        <button type="button" class="btn-icon blk-delete" aria-label="Eliminar bloque" title="Eliminar bloque" ${UIEvents.attr("click", function(event,captured){Formaciones.confirmDeleteStudyBlock(String(captured[0]),String(captured[1]));}, [category, current.id])}><i class="fas fa-trash-can"></i></button>
                        <button type="button" class="blk-btn is-primary" ${UIEvents.attr("click", function(event,captured){Formaciones.openAssignSubjectModal(String(captured[0]),String(captured[1]));}, [category, current.id])}><i class="fas fa-plus"></i> Asignar asignaturas</button>`}
                    </header>
                    <div class="blk-stats">
                        <div class="pe-stat"><span class="pe-stat-l">Asignaturas</span><span class="pe-stat-n">${current.rows.length}</span></div>
                        ${years.map((y, i) => `<div class="pe-stat"><span class="pe-stat-l">Horas ${esc(y.name)}</span><span class="pe-stat-n" data-pb-year="${i}">${current.perYear[i]}</span></div>`).join('')}
                    </div>
                    ${current.rows.length ? `
                    <div class="blk-table-wrap">
                        <table class="pe-table blk-table">
                            <thead><tr><th scope="col">Asignatura</th>${years.map(y => `<th scope="col">${esc(y.name)}</th>`).join('')}<th scope="col">Total</th><th scope="col">Formaciones</th><th scope="col">${current.loose ? 'Asignar a' : 'Mover a'}</th></tr></thead>
                            <tbody>${current.rows.map(row).join('')}</tbody>
                        </table>
                    </div>` : `<p class="blk-empty">${current.loose ? 'Todas las asignaturas tienen bloque.' : 'Este bloque aún no tiene asignaturas.'}</p>`}
                </section>
            </div>
        `;
    },

    // Keeps the figures of the Bloques tab up to date after an hours change, without re-rendering (focus stays).
    refreshStudyBlockFigures(category, blockId) {
        const list = this.studyBlockData(category).lists.find(l => l.id === blockId);
        if (!list || typeof document === 'undefined') return;
        document.querySelectorAll('[data-pb-hours]').forEach(el => { if (el.dataset.pbHours === blockId && blockId) el.textContent = `${list.hours} h`; });
        document.querySelectorAll('[data-pb-year]').forEach(el => { el.textContent = list.perYear[Number(el.dataset.pbYear)] ?? 0; });
        const totals = new Map(list.rows.map(r => [r.key, r.total]));
        document.querySelectorAll('[data-pb-total]').forEach(el => { if (totals.has(el.dataset.pbTotal)) el.textContent = `${totals.get(el.dataset.pbTotal)} h`; });
    },

    openCreateBlockModal(category) {
        const categoryLabel = Utils.sanitizeHTML(FormationTypeService.name(category));
        const presetColors = ['#0f766e', '#0284c7', '#7c3aed', '#d97706', '#e11d48', '#4f46e5', '#059669', '#475569'];

        const html = `
            <div style="text-align: left; padding: 0.5rem 0;">
                <label style="display: block; font-size: 0.82rem; font-weight: 700; color: #334155; margin-bottom: 4px;">Nombre del Bloque:</label>
                <input id="swal-block-name" class="swal2-input" placeholder="Ej: PRÁCTICAS EXTERNAS" style="width: 100%; margin: 0 0 1rem 0; box-sizing: border-box; text-transform: uppercase;">
                
                <label style="display: block; font-size: 0.82rem; font-weight: 700; color: #334155; margin-bottom: 6px;">Color Distintivo:</label>
                <div style="display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 8px;">
                    ${presetColors.map((col, idx) => `
                        <label style="cursor: pointer;">
                            <input type="radio" name="swal-block-color" value="${col}" ${idx === 0 ? 'checked' : ''} style="display: none;" ${UIEvents.attr("change", function(event,captured){document.getElementById('swal-custom-color').value=this.value;}, [])}>
                            <span style="display: inline-block; width: 28px; height: 28px; border-radius: 50%; background: ${col}; border: 2px solid white; box-shadow: 0 0 0 1px #cbd5e1; transition: transform 0.1s;" ${UIEvents.attr("mouseover", function(event,captured){this.style.transform='scale(1.1)';}, [])} ${UIEvents.attr("mouseout", function(event,captured){this.style.transform='scale(1)';}, [])}></span>
                        </label>
                    `).join('')}
                    <input type="color" id="swal-custom-color" value="#0f766e" style="width: 32px; height: 32px; border: none; border-radius: 50%; cursor: pointer; padding: 0;">
                </div>
            </div>
        `;

        SafeHTML.fire({
            title: `Nuevo Bloque (${categoryLabel})`,
            html,
            showCancelButton: true,
            confirmButtonText: 'Crear Bloque',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#0f766e',
            preConfirm: () => {
                const nameInput = document.getElementById('swal-block-name');
                const colorInput = document.getElementById('swal-custom-color');
                const name = nameInput ? nameInput.value.trim().toUpperCase() : '';
                const color = colorInput ? colorInput.value : '#0f766e';
                if (!name) {
                    Swal.showValidationMessage('El nombre del bloque es obligatorio');
                    return false;
                }
                return { name, color };
            }
        }).then(res => {
            if (res.isConfirmed && res.value) {
                DataManager.addStudyBlock(category, res.value);
                Utils.showToast('Bloque creado correctamente', 'success');
                App.render();
            }
        });
    },

    openEditBlockModal(category, blockId) {
        const blocks = this.getBlocksForCategory(category);
        const block = blocks.find(b => b.id === blockId);
        if (!block) return;

        const presetColors = ['#0f766e', '#0284c7', '#7c3aed', '#d97706', '#e11d48', '#4f46e5', '#059669', '#475569'];

        const html = `
            <div style="text-align: left; padding: 0.5rem 0;">
                <label style="display: block; font-size: 0.82rem; font-weight: 700; color: #334155; margin-bottom: 4px;">Nombre del Bloque:</label>
                <input id="swal-block-name" class="swal2-input" value="${Utils.sanitizeHTML(block.name)}" style="width: 100%; margin: 0 0 1rem 0; box-sizing: border-box; text-transform: uppercase;">
                
                <label style="display: block; font-size: 0.82rem; font-weight: 700; color: #334155; margin-bottom: 6px;">Color Distintivo:</label>
                <div style="display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 8px;">
                    ${presetColors.map(col => `
                        <label style="cursor: pointer;">
                            <input type="radio" name="swal-block-color" value="${col}" ${block.color === col ? 'checked' : ''} style="display: none;" ${UIEvents.attr("change", function(event,captured){document.getElementById('swal-custom-color').value=this.value;}, [])}>
                            <span style="display: inline-block; width: 28px; height: 28px; border-radius: 50%; background: ${col}; border: 2px solid white; box-shadow: 0 0 0 1px #cbd5e1; transition: transform 0.1s;" ${UIEvents.attr("mouseover", function(event,captured){this.style.transform='scale(1.1)';}, [])} ${UIEvents.attr("mouseout", function(event,captured){this.style.transform='scale(1)';}, [])}></span>
                        </label>
                    `).join('')}
                    <input type="color" id="swal-custom-color" value="${block.color || '#0f766e'}" style="width: 32px; height: 32px; border: none; border-radius: 50%; cursor: pointer; padding: 0;">
                </div>
            </div>
        `;

        SafeHTML.fire({
            title: `Editar Bloque`,
            html,
            showCancelButton: true,
            confirmButtonText: 'Guardar Cambios',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#0f766e',
            preConfirm: () => {
                const nameInput = document.getElementById('swal-block-name');
                const colorInput = document.getElementById('swal-custom-color');
                const name = nameInput ? nameInput.value.trim().toUpperCase() : '';
                const color = colorInput ? colorInput.value : block.color;
                if (!name) {
                    Swal.showValidationMessage('El nombre del bloque es obligatorio');
                    return false;
                }
                return { name, color };
            }
        }).then(res => {
            if (res.isConfirmed && res.value) {
                DataManager.updateStudyBlock(category, blockId, res.value);
                Utils.showToast('Bloque actualizado', 'success');
                App.render();
            }
        });
    },

    confirmDeleteStudyBlock(category, blockId) {
        const blocks = this.getBlocksForCategory(category);
        const block = blocks.find(b => b.id === blockId);
        if (!block) return;

        SafeHTML.fire({
            title: `¿Eliminar bloque "${block.name}"?`,
            text: 'Las asignaturas asociadas pasarán a «Sin bloque» de forma segura.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#dc2626'
        }).then(res => {
            if (res.isConfirmed) {
                DataManager.deleteStudyBlock(category, blockId);
                Utils.showToast('Bloque eliminado correctamente', 'success');
                App.render();
            }
        });
    },

    openAssignSubjectModal(category, blockId) {
        const blocks = this.getBlocksForCategory(category);
        const targetBlock = blocks.find(b => b.id === blockId);
        if (!targetBlock) return;

        const formations = DataManager.getFormations().filter(f => (f.type || '').toLowerCase() === category.toLowerCase());
        const formationIds = new Set(formations.map(f => String(f.id)));
        const formationsMap = {};
        formations.forEach(f => { formationsMap[String(f.id)] = f; });

        // Get subjects of this category that are NOT in targetBlock
        const subjects = DataManager.getAll(DataManager.KEYS.SUBJECTS)
            .filter(s => formationIds.has(String(s.formationId)) && !['VAC', 'EXT'].includes(s.type?.toUpperCase()))
            .filter(s => s.studyBlock !== blockId);

        if (subjects.length === 0) {
            SafeHTML.fire({
                title: 'Sin asignaturas pendientes',
                text: 'Todas las asignaturas de esta formación ya están asignadas a este bloque.',
                icon: 'info',
                confirmButtonColor: '#0f766e'
            });
            return;
        }

        const html = `
            <div style="text-align: left; max-height: 380px; overflow-y: auto; padding: 0.25rem;">
                <input type="text" id="assign-subject-search" placeholder="Filtrar por nombre de asignatura..." class="swal2-input" style="width: 100%; margin: 0 0 0.85rem 0; font-size: 0.85rem; padding: 0.5rem 0.75rem; box-sizing: border-box;" ${UIEvents.attr("input", function(event,captured){const q=this.value.toLowerCase();document.querySelectorAll('.assign-subject-row').forEach(row=>{const name=row.getAttribute('data-name')||'';row.style.display=name.includes(q)?'flex':'none';});}, [])}>
                <div style="display: flex; flex-direction: column; gap: 6px;">
                    ${subjects.map(s => {
                        const form = formationsMap[String(s.formationId)] || {};
                        const found = blocks.find(b => b.id === s.studyBlock);
                        const currentBlock = { name: found ? this.blockLabel(found) : (s.studyBlock && s.studyBlock !== 'OTROS' ? s.studyBlock : 'Sin bloque') };
                        return `
                            <label class="assign-subject-row" data-name="${Utils.sanitizeHTML(s.name.toLowerCase())}" style="display: flex; align-items: center; justify-content: space-between; padding: 8px 12px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; cursor: pointer; transition: all 0.15s;">
                                <div style="display: flex; align-items: center; gap: 10px; min-width: 0;">
                                    <input type="checkbox" name="assign-subjects" value="${Utils.sanitizeHTML(s.id)}" style="width: 16px; height: 16px; accent-color: #0f766e; cursor: pointer; flex-shrink: 0;">
                                    <div style="min-width: 0;">
                                        <div style="font-weight: 700; font-size: 0.85rem; color: #0f172a; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">${Utils.sanitizeHTML(s.name)}</div>
                                        <div style="font-size: 0.73rem; color: #64748b;">${Utils.sanitizeHTML(form.name || '')} (${form.academicYear ? form.academicYear + 'º Curso' : 'Único'}) · ${s.totalHours || 0}h</div>
                                    </div>
                                </div>
                                <span style="font-size: 0.7rem; background: #e2e8f0; color: #475569; padding: 2px 8px; border-radius: 99px; font-weight: 600; flex-shrink: 0;">
                                    ${Utils.sanitizeHTML(currentBlock.name)}
                                </span>
                            </label>
                        `;
                    }).join('')}
                </div>
            </div>
        `;

        SafeHTML.fire({
            title: `Asignar a ${Utils.sanitizeHTML(targetBlock.name)}`,
            html,
            showCancelButton: true,
            confirmButtonText: 'Asignar Seleccionadas',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#0f766e',
            width: '560px',
            preConfirm: () => {
                const checked = Array.from(document.querySelectorAll('input[name="assign-subjects"]:checked')).map(cb => cb.value);
                if (checked.length === 0) {
                    Swal.showValidationMessage('Selecciona al menos una asignatura');
                    return false;
                }
                return checked;
            }
        }).then(res => {
            if (res.isConfirmed && res.value) {
                res.value.forEach(subjectId => {
                    DataManager.assignSubjectToBlock(subjectId, blockId);
                });
                Utils.showToast(`${res.value.length} asignatura(s) asignadas con éxito`, 'success');
                App.render();
            }
        });
    },

    moveSubjectToBlock(subjectId, newBlockId, category) {
        if (!subjectId || !newBlockId) return;
        DataManager.assignSubjectToBlock(subjectId, newBlockId);
        Utils.showToast('Asignatura reasignada con éxito', 'success');
        App.render();
    },

    moveSubjectGroupToBlock(subjectKey, newBlockId, category) {
        if (!subjectKey || !newBlockId) return;
        const allSubs = DataManager.getSubjects();
        const formations = DataManager.getFormations();
        const catFormIds = new Set(formations.filter(f => f.type === category).map(f => String(f.id)));

        const matchingSubs = allSubs.filter(s => 
            catFormIds.has(String(s.formationId)) && 
            Utils.normalizeString(s.name.trim()) === subjectKey
        );

        matchingSubs.forEach(s => {
            DataManager.assignSubjectToBlock(s.id, newBlockId);
        });

        Utils.showToast(`Asignatura y sus cursos reasignados a nuevo bloque`, 'success');
        App.render();
    },

    updateSubjectHours(subjectId, newHours, category, blockId) {
        if (!subjectId) return;
        const hours = Math.max(0, parseInt(newHours) || 0);
        const subject = DataManager.getSubject(subjectId);
        if (!subject) return;

        DataManager.updateSubject(subjectId, { totalHours: hours });

        // Check if there are sibling subjects with identical name in the same category & academic year
        const allSubs = DataManager.getSubjects();
        const formations = DataManager.getFormations();
        const formationsMap = {};
        formations.forEach(f => formationsMap[String(f.id)] = f);

        const currentFormation = formationsMap[String(subject.formationId)] || {};
        const subjectNormKey = Utils.normalizeString(subject.name.trim());
        const siblings = allSubs.filter(s => {
            if (s.id === subjectId) return false;
            if (Utils.normalizeString(s.name.trim()) !== subjectNormKey) return false;
            const f = formationsMap[String(s.formationId)];
            if (!f) return false;
            return f.type === category && String(f.academicYear) === String(currentFormation.academicYear);
        });

        if (siblings.length > 0) {
            siblings.forEach(sib => {
                DataManager.updateSubject(sib.id, { totalHours: hours });
                document.querySelectorAll(`.blk-hours-input[data-subject-id="${Utils.sanitizeHTML(sib.id)}"]`).forEach(inp => {
                    inp.value = hours;
                });
            });
        }

        if (category) this.refreshStudyBlockFigures(category, blockId || '');

        const msg = siblings.length > 0
            ? `Horas de "${subject.name}" actualizadas a ${hours}h (${siblings.length + 1} especialidades sincronizadas)`
            : `Horas de "${subject.name}" actualizadas a ${hours}h`;
        Utils.showToast(msg, 'success');
    },

    // Diseño C (2026-10-26): tipos a la izquierda, la lista en el centro y la formación elegida
    // a la derecha con sus datos, cifras y asignaturas. Estilos: assets/css/views/formaciones.css (.fo-*).
    selectedFormationId: null,

    render() {
        // If we have a currentFormationId, show the details view
        if (this.currentFormationId) {
            return this.renderDetails(this.currentFormationId);
        }
        if (this.currentSearch === undefined) this.currentSearch = '';

        const allFormations = DataManager.getFormations();
        const formations = this.filteredFormations();
        if (!formations.some(f => String(f.id) === String(this.selectedFormationId))) this.selectedFormationId = formations[0]?.id ?? null;
        const types = FormationTypeService.getTypes().filter(t => t.active || allFormations.some(f => f.type === t.id));
        const students = this.studentsByFormation();
        const enrolled = new Set();
        students.forEach(list => list.forEach(id => enrolled.add(id)));
        const block = typeof FileStorage !== 'undefined' && FileStorage.classBlock ? FileStorage.classBlock(new Date()) : null;
        const summary = [
            block !== null ? `Curso ${block}/${block + 1}` : '',
            `${allFormations.length} ${allFormations.length === 1 ? 'formación' : 'formaciones'}`,
            `${enrolled.size} ${enrolled.size === 1 ? 'alumno' : 'alumnos'}`
        ].filter(Boolean).join(' · ');
        const typeButton = (key, label, count, color) => {
            const on = this.currentFilter === key;
            return `
                <button type="button" class="filter-item fo-type ${on ? 'active' : ''}" aria-pressed="${on}" ${UIEvents.attr("click", function(event,captured){Formaciones.setFilter(String(captured[0]));}, [key])}>
                    <span class="fo-type-name"><span class="fo-dot" style="background: ${color};" aria-hidden="true"></span>${Utils.sanitizeHTML(label)}</span>
                    <span class="filter-count">${count}</span>
                </button>`;
        };

        return `
            <div class="dnr-proposal fo-view">
                <header class="fo-head">
                    <div>
                        <h1 class="fo-title">Formaciones</h1>
                        <p class="fo-sub">${Utils.sanitizeHTML(summary)}</p>
                    </div>
                    <button type="button" class="fo-btn fo-btn-primary" ${UIEvents.attr("click", function(event,captured){Formaciones.showCreateForm();}, [])}>+ Nueva formación</button>
                </header>

                <div class="formations-layout fo-layout">
                    <nav class="formations-sidebar fo-types" aria-label="Tipos de formación">
                        <span class="formations-sidebar-title">Tipo</span>
                        ${typeButton('all', 'Todas', allFormations.length, 'var(--dnr-line-strong)')}
                        ${types.map(t => typeButton(t.id, FormationTypeService.plural(t.id), allFormations.filter(f => f.type === t.id).length, this.typeColor(t.id))).join('')}
                    </nav>

                    <section class="fo-card fo-list-card" aria-label="Formaciones">
                        <div class="fo-search">
                            <label for="formation-search-input" class="fo-sr-only">Buscar formación</label>
                            <input type="search" id="formation-search-input" placeholder="Buscar formación…" value="${Utils.sanitizeHTML(this.currentSearch)}" ${UIEvents.attr("input", function(event,captured){Formaciones.handleSearch(this.value);}, [])}>
                        </div>
                        <div class="formations-list fo-list" id="fo-list">
                            ${this.renderFormationsListContent(formations)}
                        </div>
                    </section>

                    <aside class="fo-card fo-detail" id="fo-detail" aria-live="polite">
                        ${this.renderFormationDetail(allFormations.find(f => String(f.id) === String(this.selectedFormationId)))}
                    </aside>
                </div>
            </div>
        `;
    },

    // Type colour (Plan de Estudios → Tipos); validated as #rrggbb before it reaches a style.
    typeColor(type) {
        const color = FormationTypeService.color(type);
        return /^#[0-9a-f]{6}$/i.test(color) ? color : 'var(--dnr-teal-700)';
    },

    filteredFormations() {
        const allFormations = DataManager.getFormations();
        let formations = this.currentFilter === 'all' ? allFormations : allFormations.filter(f => f.type === this.currentFilter);
        const query = (this.currentSearch || '').trim().toLowerCase();
        if (query) {
            formations = formations.filter(f =>
                (f.name || '').toLowerCase().includes(query) ||
                (f.commercialName && f.commercialName.toLowerCase().includes(query)) ||
                (f.description && f.description.toLowerCase().includes(query))
            );
        }
        return formations;
    },

    /** Map formationId → set of active student ids. */
    studentsByFormation() {
        const map = new Map();
        (DataManager.getAll(DataManager.KEYS.STUDENTS) || []).forEach(s => {
            if (s.active === false) return;
            (s.formations || []).forEach(fid => {
                const key = String(fid);
                if (!map.has(key)) map.set(key, new Set());
                map.get(key).add(String(s.id));
            });
        });
        return map;
    },

    setFilter(filter) {
        this.currentFilter = filter;
        App.navigate('formaciones'); // Re-render
    },

    handleSearch(query) {
        this.currentSearch = query;
        const list = document.getElementById('fo-list');
        if (list) SafeHTML.set(list, this.renderFormationsListContent(this.filteredFormations()));
    },

    selectFormation(id) {
        this.selectedFormationId = id;
        document.querySelectorAll('#fo-list .fo-row').forEach(row => {
            const on = row.dataset.id === String(id);
            row.classList.toggle('is-on', on);
            row.setAttribute('aria-pressed', String(on));
        });
        const box = document.getElementById('fo-detail');
        if (!box) return;
        SafeHTML.set(box, this.renderFormationDetail(DataManager.getFormations().find(f => String(f.id) === String(id))));
        // On a phone the record sits under the list: bring it into view.
        if (window.matchMedia?.('(max-width: 860px)').matches) box.scrollIntoView({behavior: 'smooth', block: 'start'});
    },

    renderFormationsListContent(formations) {
        if (formations.length === 0) {
            return `<p class="fo-empty">No hay formaciones con estos filtros.</p>`;
        }
        const students = this.studentsByFormation();
        return formations.map(formation => this.renderFormationRow(formation, students)).join('');
    },

    renderFormationRow(formation, students = this.studentsByFormation()) {
        const on = String(formation.id) === String(this.selectedFormationId);
        const subjects = DataManager.getSubjectsByFormation(formation.id).length;
        const count = students.get(String(formation.id))?.size || 0;
        return `
            <button type="button" class="formation-row fo-row ${on ? 'is-on' : ''}" data-id="${Utils.sanitizeHTML(formation.id)}" aria-pressed="${on}" style="--fo-color: ${this.typeColor(formation.type)};"
                ${UIEvents.attr("click", function(event,captured){Formaciones.selectFormation(String(captured[0]));}, [formation.id])}>
                <span class="fo-row-text">
                    <strong class="formation-row-title">${Utils.sanitizeHTML(formation.name)}</strong>
                    <span class="fo-row-meta">${Utils.sanitizeHTML(this.getFormationTypeLabel(formation.type))} · ${subjects} ${subjects === 1 ? 'asignatura' : 'asignaturas'} · ${count} ${count === 1 ? 'alumno' : 'alumnos'}</span>
                </span>
                <span class="fo-row-go" aria-hidden="true">›</span>
            </button>
        `;
    },

    renderFormationDetail(formation) {
        if (!formation) {
            return `<p class="fo-empty">Elige una formación de la lista o crea una nueva.</p>`;
        }
        const id = String(formation.id);
        const subjects = DataManager.getSubjectsByFormation(formation.id);
        const students = this.studentsByFormation().get(id)?.size || 0;
        const formationSummary = this.currentClassSummary('formation');
        const classes = formationSummary ? (formationSummary.get(id)?.count || 0) : '…';
        const subjectSummary = this.currentClassSummary('subject');
        const hasPlan = formation.studyPlanId && typeof StudyPlanService !== 'undefined';
        const plan = (hasPlan && StudyPlanService.label(formation.studyPlanId)) || 'Sin plan de estudios';
        const years = hasPlan ? StudyPlanService.years(formation.studyPlanId, formation.type) : [];
        const year = formation.academicYear ? (years.find(y => String(y.id) === String(formation.academicYear))?.name || `${formation.academicYear}º`) : 'Único';
        const time = formation.defaultStartTime && formation.defaultEndTime ? `${formation.defaultStartTime}–${formation.defaultEndTime}` : 'Sin horario general';
        const professorName = pid => {
            const p = pid ? DataManager.getProfessor(pid) : null;
            return p ? `${p.firstName || ''} ${p.lastName || ''}`.trim() : 'Sin profesor';
        };
        const shown = subjects.slice(0, 8);
        const hours = s => {
            const total = Number(s.totalHours) || 0;
            const done = subjectSummary ? Math.round(subjectSummary.get(String(s.id))?.hours || 0) : null;
            if (done === null) return total ? `${total} h` : '';
            return total ? `${done} / ${total} h` : `${done} h`;
        };
        const stat = (value, label) => `<div class="fo-stat"><span class="fo-stat-n">${value}</span><span class="fo-stat-l">${label}</span></div>`;
        return `
            <div class="fo-detail-in" style="--fo-color: ${this.typeColor(formation.type)};">
                <div>
                    <span class="fo-kicker">${Utils.sanitizeHTML(this.getFormationTypeLabel(formation.type))}</span>
                    <h2 class="fo-detail-title">${Utils.sanitizeHTML(formation.name)}</h2>
                    ${formation.description ? `<p class="fo-desc">${Utils.sanitizeHTML(formation.description)}</p>` : ''}
                </div>
                <dl class="fo-facts">
                    <dt>Plan de estudios</dt><dd>${Utils.sanitizeHTML(plan)}</dd>
                    <dt>Curso</dt><dd>${Utils.sanitizeHTML(year)}</dd>
                    <dt>Horario</dt><dd>${Utils.sanitizeHTML(time)}</dd>
                    <dt>Nombre comercial</dt><dd>${formation.commercialName ? Utils.sanitizeHTML(formation.commercialName) : '—'}</dd>
                </dl>
                <div class="fo-stats">
                    ${stat(subjects.length, subjects.length === 1 ? 'asignatura' : 'asignaturas')}
                    ${stat(students, students === 1 ? 'alumno' : 'alumnos')}
                    ${stat(classes, 'clases este curso')}
                </div>
                <div>
                    <h3 class="fo-h3">Asignaturas</h3>
                    ${subjects.length ? shown.map(s => `
                        <div class="fo-subject">
                            <span>${Utils.sanitizeHTML(s.name)}</span>
                            <span class="fo-subject-prof">${Utils.sanitizeHTML(professorName(s.professorId))}</span>
                            <span class="fo-subject-h">${hours(s)}</span>
                        </div>`).join('') : '<p class="fo-empty fo-empty-inline">Aún no tiene asignaturas.</p>'}
                    ${subjects.length > shown.length ? `<p class="fo-more">y ${subjects.length - shown.length} más en «Gestionar»</p>` : ''}
                </div>
                <div class="fo-actions">
                    <button type="button" class="fo-btn fo-btn-primary" ${UIEvents.attr("click", function(event,captured){Formaciones.viewDetails(String(captured[0]));}, [formation.id])}>Gestionar</button>
                    <button type="button" class="fo-btn" ${UIEvents.attr("click", function(event,captured){Formaciones.showAddSubjectForm(String(captured[0]));}, [formation.id])}>+ Asignatura</button>
                    <button type="button" class="fo-btn" ${UIEvents.attr("click", function(event,captured){Formaciones.showEditForm(String(captured[0]));}, [formation.id])}>Editar</button>
                    <button type="button" class="fo-btn fo-btn-danger" ${UIEvents.attr("click", function(event,captured){Formaciones.deleteFormation(String(captured[0]));}, [formation.id])}>Eliminar</button>
                </div>
            </div>
        `;
    },

    getFormationTypeBadge(type) {
        const color = FormationTypeService.color(type);
        const safe = /^#[0-9a-f]{6}$/i.test(color) ? color : '#64748b';
        return `<span class="badge" style="background: ${safe};">${Utils.sanitizeHTML(this.getFormationTypeLabel(type))}</span>`;
    },

    getFormationTypeLabel(type) {
        return FormationTypeService.name(type);
    },

    // <option>s of the active types (plus the current one if it was deactivated).
    typeOptions(selected = '') {
        const types = FormationTypeService.getTypes().filter(t => t.active || t.id === selected);
        return types.map(t => `<option value="${Utils.sanitizeHTML(t.id)}" ${t.id === selected ? 'selected' : ''}>${Utils.sanitizeHTML(t.name)}${t.active ? '' : ' (desactivado)'}</option>`).join('');
    },

    showCreateForm() {
        if (!AuthManager.requireModuleManage("formaciones",["admin","coordinator"])) return;
        const content = `
            <form id="formation-form" class="dnr-proposal fmtd-form-dnr" ${UIEvents.attr("submit", function(event,captured){Formaciones.handleSubmit(event);}, [])}>
                <div class="form-group">
                    <label class="form-label">Tipo de Formación *</label>
                    <select class="form-select" name="type" required ${UIEvents.attr("change", function(event,captured){Formaciones.handleTypeChange(this);}, [])}>
                        <option value="">Seleccionar tipo...</option>
                        ${this.typeOptions()}
                    </select>
                </div>

                <div class="form-group grid grid-2" style="gap: 1rem;">
                    <div>
                        <label class="form-label">Plan de Estudios</label>
                        <select class="form-select" name="studyPlanId" id="create-study-plan" ${UIEvents.attr("change", function(event,captured){Formaciones.handleStudyPlanChange(this);}, [])}>
                            <option value="">Seleccionar tipo primero...</option>
                        </select>
                    </div>
                    <div>
                        <label class="form-label">Curso Académico</label>
                        <select class="form-select" name="academicYear" id="create-academic-year">
                            <option value="">Seleccionar tipo primero...</option>
                        </select>
                    </div>
                </div>

                 <div class="form-group">
                    <label class="form-label">Nombre * (Interno, para gestión)</label>
                    <input type="text" class="form-input" name="name" required>
                </div>
                
                <div class="form-group">
                    <label class="form-label">Nombre Comercial (para los correos a Contactos)</label>
                    <input type="text" class="form-input" name="commercialName" placeholder="Nombre con el que se anuncia la formación">
                </div>
                
                <div class="form-group grid grid-2" style="gap: 1rem;">
                    <div>
                        <label class="form-label">Horario General Inicio</label>
                        <input type="time" class="form-input" name="defaultStartTime">
                    </div>
                    <div>
                        <label class="form-label">Horario General Fin</label>
                        <input type="time" class="form-input" name="defaultEndTime">
                    </div>
                </div>

                <div class="form-group">
                    <label class="form-label">Descripción</label>
                    <textarea class="form-textarea" name="description"></textarea>
                </div>
                
                <div class="flex gap-1" style="justify-content: flex-end; margin-top: 1.5rem;">
                    <button type="button" class="btn btn-secondary" ${UIEvents.attr("click", function(event,captured){Utils.closeModal();}, [])}>
                        Cancelar
                    </button>
                    <button type="submit" class="btn btn-primary">
                        <i class="fas fa-save"></i> Guardar
                    </button>
                </div>
            </form>
        `;

        Utils.openModal('Nueva Formación', content);
    },

    showEditForm(formationId) {
        if (!AuthManager.requireModuleManage("formaciones",["admin","coordinator"])) return;
        const formation = DataManager.getFormation(formationId);
        if (!formation) return;

        const content = `
            <form id="formation-form" class="dnr-proposal fmtd-form-dnr">
                <input type="hidden" name="_formationId" value="${formationId}">
                <div class="form-group">
                    <label class="form-label">Tipo de Formación *</label>
                    <select class="form-select" name="type" required ${UIEvents.attr("change", function(event,captured){Formaciones.handleTypeChange(this);}, [])}>
                        <option value="">Seleccionar tipo...</option>
                        ${this.typeOptions(formation.type)}
                    </select>
                </div>

                <div class="form-group grid grid-2" style="gap: 1rem;">
                    <div>
                        <label class="form-label">Plan de Estudios</label>
                        <select class="form-select" name="studyPlanId" id="edit-study-plan" ${UIEvents.attr("change", function(event,captured){Formaciones.handleStudyPlanChange(this);}, [])}>
                             <!-- Options populated by JS -->
                        </select>
                    </div>
                    <div>
                        <label class="form-label">Curso Académico</label>
                        <select class="form-select" name="academicYear" id="edit-academic-year">
                             <!-- Options populated by JS -->
                        </select>
                    </div>
                </div>

                 <div class="form-group">
                    <label class="form-label">Nombre * (Interno, para gestión)</label>
                    <input type="text" class="form-input" name="name" value="${Utils.sanitizeHTML(formation.name)}" required>
                </div>
                
                <div class="form-group">
                    <label class="form-label">Nombre Comercial (para los correos a Contactos)</label>
                    <input type="text" class="form-input" name="commercialName" value="${Utils.sanitizeHTML(formation.commercialName || '')}" placeholder="Nombre con el que se anuncia la formación">
                </div>
                
                <div class="form-group grid grid-2" style="gap: 1rem;">
                    <div>
                        <label class="form-label">Horario General Inicio</label>
                        <input type="time" class="form-input" name="defaultStartTime" value="${formation.defaultStartTime || ''}">
                    </div>
                    <div>
                        <label class="form-label">Horario General Fin</label>
                        <input type="time" class="form-input" name="defaultEndTime" value="${formation.defaultEndTime || ''}">
                    </div>
                </div>

                <div class="form-group">
                    <label class="form-label">Descripción</label>
                    <textarea class="form-textarea" name="description">${Utils.sanitizeHTML(formation.description || '')}</textarea>
                </div>
                
                <div class="flex gap-1" style="justify-content: flex-end; margin-top: 1.5rem;">
                    <button type="button" class="btn btn-secondary" ${UIEvents.attr("click", function(event,captured){Utils.closeModal();}, [])}>
                        Cancelar
                    </button>
                    <button type="button" class="btn btn-primary" ${UIEvents.attr("click", function(event,captured){Formaciones.handleUpdate(String(captured[0]));}, [formationId])}>
                        <i class="fas fa-save"></i> Actualizar
                    </button>
                </div>
            </form>
        `;

        Utils.openModal('Editar Formación', content);

        // Initialize options for the current formation state
        const form = document.getElementById('formation-form');
        const typeSelect = form.querySelector('select[name="type"]');
        const studyPlanSelect = form.querySelector('select[name="studyPlanId"]');
        const academicYearSelect = form.querySelector('select[name="academicYear"]');

        if (typeSelect && formation.type) {
            this.updateFormOptions(formation.type, studyPlanSelect, academicYearSelect, formation.studyPlanId, formation.academicYear);
        }
    },

    handleTypeChange(selectElement) {
        const form = selectElement.closest('form');
        const type = selectElement.value;
        const studyPlanSelect = form.querySelector('select[name="studyPlanId"]');
        const academicYearSelect = form.querySelector('select[name="academicYear"]');

        this.updateFormOptions(type, studyPlanSelect, academicYearSelect);
    },

    handleStudyPlanChange(selectElement) {
        // Courses: the plan's own (Plan de Estudios → Planes), else its type's (→ Tipos).
        const form = selectElement.closest('form');
        const typeSelect = form.querySelector('select[name="type"]');
        const academicYearSelect = form.querySelector('select[name="academicYear"]');
        const years = selectElement.value ? StudyPlanService.years(selectElement.value, typeSelect.value) : FormationTypeService.years(typeSelect.value);
        const current = academicYearSelect.value;
        SafeHTML.set(academicYearSelect, years.length
            ? years.map(y => `<option value="${Utils.sanitizeHTML(y.id)}" ${y.id === current ? 'selected' : ''}>${Utils.sanitizeHTML(y.name)}</option>`).join('')
            : '<option value="">No aplica</option>');
    },

    updateFormOptions(type, studyPlanSelect, academicYearSelect, selectedStudyPlanId = null, selectedYearId = null) {
        if (!this.FORMATION_CONFIG[type]) return;

        const config = this.FORMATION_CONFIG[type];

        // Update Study Plans
        let studyPlanHtml = '<option value="">Seleccionar...</option>';
        // A formation keeps its plan even if that plan was deactivated (shown as such).
        const plans = [...config.studyPlans];
        if (selectedStudyPlanId && !plans.some(p => p.id === selectedStudyPlanId)) {
            const current = this.STUDY_PLANS.find(p => p.id === selectedStudyPlanId);
            plans.push({id: selectedStudyPlanId, name: current ? StudyPlanService.label(selectedStudyPlanId) : selectedStudyPlanId});
        }
        if (plans.length > 0) {
            studyPlanHtml += plans.map(p =>
                `<option value="${Utils.sanitizeHTML(p.id)}" ${selectedStudyPlanId === p.id ? 'selected' : ''}>${Utils.sanitizeHTML(p.name)}</option>`
            ).join('');
        }
        SafeHTML.set(studyPlanSelect, studyPlanHtml);

        // Update Academic Years: the plan's own courses, else the type's.
        const years = selectedStudyPlanId ? StudyPlanService.years(selectedStudyPlanId, type) : config.years;
        let yearHtml = '';
        if (years.length > 0) {
            yearHtml += years.map(y =>
                `<option value="${Utils.sanitizeHTML(y.id)}" ${selectedYearId === y.id ? 'selected' : ''}>${Utils.sanitizeHTML(y.name)}</option>`
            ).join('');
        } else {
            yearHtml = '<option value="">No aplica</option>';
        }
        SafeHTML.set(academicYearSelect, yearHtml);
    },

    handleSubmit(event) {
        event.preventDefault();
        if (!AuthManager.requireModuleManage("formaciones",["admin","coordinator"])) return;
        const formData = new FormData(event.target);
        const data = Object.fromEntries(formData);

        DataManager.createFormation(data);
        Utils.closeModal();
        Utils.showToast('Formación creada correctamente', 'success');
        App.navigate('formaciones');
    },

    async handleUpdate(formationId) {
        if (!AuthManager.requireModuleManage("formaciones",["admin","coordinator"])) return;
        const form = document.getElementById('formation-form');
        if (!form) return;
        const formData = new FormData(form);
        const data = Object.fromEntries(formData);
        // Eliminar el campo interno de control
        delete data._formationId;

        // Llamada directa a la API REST para garantizar que commercialName se persiste
        try {
            const token = AuthManager.getCsrfToken();
            const response = await fetch(`/api/formations/${formationId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'X-SICAF-CSRF': token
                },
                body: JSON.stringify(data)
            });
            const result = await response.json();
            if (result.success) {
                // Actualizar también el cache local con el objeto devuelto por el servidor
                DataManager.updateFormation(formationId, result.formation);
                Utils.closeModal();
                Utils.showToast('Formación actualizada correctamente', 'success');
                App.navigate('formaciones');
            } else {
                Utils.showToast('Error al actualizar la formación: ' + (result.error || ''), 'error');
            }
        } catch (err) {
            console.error('Error al actualizar formación:', err);
            // Fallback al método antiguo
            DataManager.updateFormation(formationId, data);
            Utils.closeModal();
            Utils.showToast('Formación actualizada', 'success');
            App.navigate('formaciones');
        }
    },

    deleteFormation(formationId) {
        if (!AuthManager.requireModuleManage("formaciones",["admin","coordinator"])) return;
        const formation = DataManager.getFormation(formationId);
        if (!formation) return;

        const subjects = DataManager.getSubjectsByFormation(formationId);

        Utils.showConfirm(
            `¿Estás seguro de que quieres eliminar "${formation.name}"? Esto también eliminará ${subjects.length} asignatura(s) y todas las clases asociadas.`,
            () => {
                DataManager.deleteFormation(formationId);
                Utils.showToast('Formación eliminada correctamente', 'success');
                App.navigate('formaciones');
            }
        );
    },

    viewDetails(formationId) {
        this.currentFormationId = formationId;
        App.navigate('formaciones');
    }
};

// Make Formaciones globally available
window.Formaciones = Formaciones;
